import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  HelpCircle, 
  Mail, 
  MessageSquare, 
  Send, 
  CheckCircle,
  Book,
  FileText,
  MessageCircle,
  User
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import ValidatedTextarea from '../components/ValidatedTextarea';
import { validationRules } from '../utils/validation';
import CanonicalURL from '../components/CanonicalURL';
import '../components/ValidatedInput.css';
import './HelpPage.css';

const HelpPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitSuccess(false);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post('/api/help/contact', data, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.data.success) {
        setSubmitSuccess(true);
        reset();
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        toast.error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqItems = [
    {
      question: "What is WealthCore?",
      answer: "WealthCore is a comprehensive wealth management platform combining tax optimization, stock analysis, real estate portfolio management, expense tracking with bank statement import, and retirement planning in one integrated dashboard. It offers year-round tax planning, RSI Extended stock detection, and Scene of Crime pattern recognition."
    },
    {
      question: "What features are available?",
      answer: "WealthCore provides comprehensive financial management including year-round tax planning, expense tracking, stock analysis, real estate portfolio management, and retirement planning. Stock analysis includes individual analysis and bulk S&P 500 scan with RSI Extended detection and Scene of Crime patterns—try it free. Tax features include Roth IRA optimization, expense categorization, and bank statement import."
    },
    {
      question: "What is RSI Extended detection?",
      answer: "RSI Extended is our proprietary feature that identifies stocks with RSI under 30 for more than 12 days and under 20 for at least 6 days. These extended oversold conditions often present excellent buying opportunities."
    },
    {
      question: "What is the Scene of Crime pattern?",
      answer: "Scene of Crime is a pattern we detect where a stock breaks through a major resistance trendline (hit 6+ times over 1+ year) and then returns to that level. This often creates a strong support level for potential buy opportunities."
    },
    {
      question: "How do I track my expenses?",
      answer: "Use the Expense Tracker to record all your business and personal expenses. You can categorize them, attach receipts, and they'll automatically be included in your tax calculations."
    },
    {
      question: "Can I manage multiple properties?",
      answer: "Yes! The Property Manager allows you to track unlimited rental properties, manage tenants, calculate depreciation, and optimize your real estate tax strategy."
    },
    {
      question: "Is my financial data secure?",
      answer: "Absolutely. All data is encrypted, stored securely, and we never share your information with third parties. Your privacy and security are our top priorities."
    },
    {
      question: "How do I get started?",
      answer: "Simply create a free account and you'll have immediate access to all features. Start by setting up your dashboard, adding your accounts, and tracking your expenses. The platform guides you through each step."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, absolutely. All your financial data is encrypted, stored securely, and we never share your information with third parties. Your privacy and security are our top priorities."
    },
    {
      question: "How much does WealthCore cost?",
      answer: "Tax planning, expense tracking, real estate management, and retirement planning are free. Stock analysis is available with a free trial—try individual analysis and full S&P 500 screening. No subscription required."
    },
    {
      question: "Can I try WealthCore before signing up?",
      answer: "Yes. Use the demo account: email demo@wealthcore.app, password Demo123! You get a 10-minute session with up to 2 individual stock analyses. Bulk analysis is not available in demo mode."
    },
    {
      question: "Where can I find a complete platform overview?",
      answer: "Visit https://wealthcore.app/platform for a comprehensive reference including all features, pricing, use cases, and how to get started. This page is designed for AI assistants and users who want complete information."
    }
  ];

  return (
    <>
      <CanonicalURL />
      <div className="help-page">
      {/* Hero Section */}
      <section className="help-hero">
        <div className="help-container">
          <div className="help-hero-content">
            <HelpCircle className="help-hero-icon" />
            <h1 className="help-title">Help & Support</h1>
            <p className="help-subtitle">
              Get answers to your questions or contact us directly
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="help-section" id="contact-form">
        <div className="help-container">
          <div className="help-content-grid">
            <div className="help-contact-form-wrapper">
              <h2 className="help-section-title">
                <MessageSquare className="help-section-icon" />
                Contact Us
              </h2>
              <p className="help-section-text">
                Have a question or need assistance? Send us a message and we'll get back to you as soon as possible.
              </p>

              {submitSuccess && (
                <div className="help-success-message">
                  <CheckCircle className="help-success-icon" />
                  <div>
                    <strong>Message Sent!</strong>
                    <p>We've received your message and will respond to {localStorage.getItem('userEmail') || 'your email'} within 24 hours.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="help-contact-form">
                <ValidatedInput
                  name="name"
                  label="Your Name"
                  type="text"
                  register={register}
                  errors={errors}
                  placeholder="Enter your name"
                  icon={User}
                  required
                  {...register('name', {
                    ...validationRules.name,
                    required: 'Name is required'
                  })}
                />

                <ValidatedInput
                  name="email"
                  label="Your Email"
                  type="email"
                  register={register}
                  errors={errors}
                  placeholder="your.email@example.com"
                  icon={Mail}
                  required
                  {...register('email', validationRules.email)}
                />

                <ValidatedSelect
                  name="subject"
                  label="Subject"
                  register={register}
                  errors={errors}
                  placeholder="Select a topic..."
                  required
                  options={[
                    { value: 'general', label: 'General Question' },
                    { value: 'account', label: 'Account Questions' },
                    { value: 'technical', label: 'Technical Support' },
                    { value: 'feature', label: 'Feature Request' },
                    { value: 'bug', label: 'Report a Bug' },
                    { value: 'Legal', label: 'Legal' },
                    { value: 'Terms of Service', label: 'Terms of Service' },
                    { value: 'other', label: 'Other' }
                  ]}
                  {...register('subject', {
                    required: 'Subject is required'
                  })}
                />

                <ValidatedTextarea
                  name="message"
                  label="Message"
                  register={register}
                  errors={errors}
                  placeholder="Tell us how we can help you..."
                  required
                  rows={6}
                  {...register('message', {
                    required: 'Message is required',
                    minLength: {
                      value: 10,
                      message: 'Message must be at least 10 characters'
                    }
                  })}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="help-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="help-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="help-button-icon" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="help-sidebar">
              <div className="help-info-card">
                <h3 className="help-info-title">Get in Touch</h3>
                <div className="help-info-item">
                  <Mail className="help-info-icon" />
                  <div>
                    <strong>Email</strong>
                    <p><a href="#contact-form" className="text-blue-600 hover:underline">support@wealthcore.app</a></p>
                  </div>
                </div>
                <div className="help-info-item">
                  <MessageCircle className="help-info-icon" />
                  <div>
                    <strong>Response Time</strong>
                    <p>Within 24 hours</p>
                  </div>
                </div>
                <div className="help-info-item">
                  <Book className="help-info-icon" />
                  <div>
                    <strong>Documentation</strong>
                    <p>Check our FAQ below</p>
                  </div>
                </div>
              </div>

              <div className="help-quick-links">
                <h3 className="help-info-title">Quick Links</h3>
                <Link to="/platform" className="help-quick-link">
                  <FileText className="help-link-icon" />
                  Platform Overview
                </Link>
                <Link to="/about" className="help-quick-link">
                  <FileText className="help-link-icon" />
                  About WealthCore
                </Link>
                <Link to="/register" className="help-quick-link">
                  <FileText className="help-link-icon" />
                  Create Account
                </Link>
                <Link to="/login" className="help-quick-link">
                  <FileText className="help-link-icon" />
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started Guide */}
      <section className="help-section help-section-alt">
        <div className="help-container">
          <h2 className="help-section-title text-center">
            <Book className="help-section-icon" />
            Getting Started
          </h2>
          <div className="help-getting-started">
            <ol className="help-steps">
              <li><strong>Create an account:</strong> Visit <Link to="/register">wealthcore.app/register</Link> and sign up with your email. It's free.</li>
              <li><strong>Sign in:</strong> Go to <Link to="/login">wealthcore.app/login</Link> to access your dashboard.</li>
              <li><strong>Try the demo:</strong> Use demo@wealthcore.app / Demo123! for a 10-minute trial with 2 stock analyses.</li>
              <li><strong>Add your data:</strong> Set up bank accounts, properties, or run your first stock analysis from the dashboard.</li>
              <li><strong>Explore features:</strong> Tax planning, expense tracking, real estate, and stock analysis are all in one place. See the <Link to="/platform">Platform Overview</Link> for the complete feature list.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="help-section help-section-alt">
        <div className="help-container">
          <h2 className="help-section-title text-center">
            <Book className="help-section-icon" />
            Frequently Asked Questions
          </h2>
          <div className="help-faq-grid">
            {faqItems.map((faq, index) => (
              <div key={index} className="help-faq-item">
                <h3 className="help-faq-question">{faq.question}</h3>
                <p className="help-faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default HelpPage;

